import { Link as RouterLink } from "react-router-dom";
import { Box, Chip, Divider, Stack, Typography } from "@mui/material";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import StoreMallDirectoryRoundedIcon from "@mui/icons-material/StoreMallDirectoryRounded";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import type { ClientOrder } from "../services/clientApi.service";
import { getClientOrderStatusInfo } from "../utils/orderStatus";

export interface ClientOrderCardProps {
  order: ClientOrder;
  /** Inclut l'année dans la date (utile pour l'historique). */
  showYearInDate?: boolean;
}

/**
 * Carte commande cliquable du portail client (accueil, liste…).
 */
export function ClientOrderCard({
  order,
  showYearInDate = false,
}: ClientOrderCardProps): JSX.Element {
  const statusInfo = getClientOrderStatusInfo(order.status, order.serviceType);
  const datePattern = showYearInDate ? "EEEE d MMMM yyyy" : "EEEE d MMMM";

  return (
    <Box
      component={RouterLink}
      to={`/client/orders/${order.id}`}
      sx={{
        textDecoration: "none",
        color: "inherit",
        display: "block",
        borderRadius: 1,
        mx: -1,
        px: 1,
        py: 0.5,
        transition: "background-color 0.15s",
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Stack spacing={1.5} sx={{ py: 1 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          {order.serviceType === "CATERING" ? (
            <StoreMallDirectoryRoundedIcon color="primary" />
          ) : (
            <LocalShippingRoundedIcon color="primary" />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={1}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {format(parseISO(order.deliveryDate), datePattern, { locale: fr })}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, flexShrink: 0 }}>
                {order.totalAmount.toFixed(2)} €
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block">
              {order.serviceType === "CATERING" ? "Retrait sur place" : "Livraison à domicile"}
              {order.deliveryTimeSlot ? ` · ${order.deliveryTimeSlot}` : ""}
            </Typography>
            <Chip
              size="small"
              sx={{ mt: 0.75 }}
              color={statusInfo.color}
              label={statusInfo.label}
            />
          </Box>
        </Stack>
        <Divider />
        <Typography variant="caption" color="text.secondary">
          N° {order.orderNumber} · {order.items.length} article(s)
        </Typography>
      </Stack>
    </Box>
  );
}
